import { useState, useCallback } from 'react';
import { 
  userRegistrationService, 
  UserRegistrationData, 
  UserRegistrationResult,
  ValidationErrors 
} from '@/services/userRegistrationService';

/**
 * Registration states
 */
type RegistrationState = 'idle' | 'registering' | 'success' | 'error';

/**
 * User registration hook return type
 */
interface UseUserRegistrationReturn {
  // State
  state: RegistrationState;
  isRegistering: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: string | null;
  result: UserRegistrationResult | null;
  validationErrors: ValidationErrors;

  // Actions
  registerWithEmail: (userData: UserRegistrationData) => Promise<UserRegistrationResult>;
  registerWithGoogle: () => Promise<UserRegistrationResult>;
  clearError: () => void;
  clearValidationErrors: () => void;
  reset: () => void;
}

/**
 * Custom hook for user registration state management
 */
export function useUserRegistration(): UseUserRegistrationReturn {
  const [state, setState] = useState<RegistrationState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UserRegistrationResult | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  // Derived state
  const isRegistering = state === 'registering';
  const isSuccess = state === 'success';
  const isError = state === 'error';

  /**
   * Register user with email and password
   */
  const registerWithEmail = useCallback(async (userData: UserRegistrationData): Promise<UserRegistrationResult> => {
    try {
      setState('registering');
      setError(null);
      setValidationErrors({});

      const registrationResult = await userRegistrationService.registerWithEmail(userData);

      if (registrationResult.success) {
        setState('success');
        setResult(registrationResult);
      } else {
        setState('error');
        setError(registrationResult.error || 'Registration failed');
        
        // If it's a validation error, try to parse it
        if (registrationResult.error?.includes('required') || 
            registrationResult.error?.includes('invalid') ||
            registrationResult.error?.includes('characters')) {
          // This is a simple validation error - could enhance with more sophisticated parsing
          setValidationErrors({ email: registrationResult.error });
        }
      }

      return registrationResult;

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setState('error');
      setError(errorMessage);
      
      return {
        success: false,
        error: errorMessage
      };
    }
  }, []);

  /**
   * Register user with Google OAuth
   */
  const registerWithGoogle = useCallback(async (): Promise<UserRegistrationResult> => {
    try {
      setState('registering');
      setError(null);
      setValidationErrors({});

      const registrationResult = await userRegistrationService.registerWithGoogle();

      if (registrationResult.success) {
        setState('success');
        setResult(registrationResult);
      } else {
        setState('error');
        setError(registrationResult.error || 'Google registration failed');
      }

      return registrationResult;

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Google registration failed';
      setState('error');
      setError(errorMessage);
      
      return {
        success: false,
        error: errorMessage
      };
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
    if (state === 'error') {
      setState('idle');
    }
  }, [state]);

  /**
   * Clear validation errors
   */
  const clearValidationErrors = useCallback(() => {
    setValidationErrors({});
  }, []);

  /**
   * Reset all state to initial values
   */
  const reset = useCallback(() => {
    setState('idle');
    setError(null);
    setResult(null);
    setValidationErrors({});
  }, []);

  return {
    // State
    state,
    isRegistering,
    isSuccess,
    isError,
    error,
    result,
    validationErrors,

    // Actions
    registerWithEmail,
    registerWithGoogle,
    clearError,
    clearValidationErrors,
    reset
  };
}
